import type { PaymentSessionResource, PaymentSessionTender } from '@/lib/api/api.gen'
import { normalizeCurrencyValue, parseCurrency } from '@/lib/utils'

/**
 * Pay Now as a state machine, following the Payments Foundation plan's state table. A state changes only through a
 * row in `PAY_NOW_TRANSITIONS`, each state carries the data it needs (the submitting state carries the amount it
 * charges, so no screen can lose it mid-payment), and the server's payment session is the only source of amounts,
 * limits and attempt outcomes. A submit whose answer never arrives is never treated as failed: Pay Now asks again
 * with the same submission key, which the server treats as the same request.
 */

export type PaymentSession = PaymentSessionResource

/** How staff collect: a card typed into Pay Now, a card reader, cash, or a check. */
export type PayNowTender = PaymentSessionTender | 'cash' | 'check'

interface Charging {
  session: PaymentSession
  tender: PayNowTender
  /** What this payment puts toward the target, without any shop fee. The session's limits apply to it. */
  amount: number
  /**
   * What the customer pays for it: the amount itself, or with the shop fee the server quoted when this tender carries
   * one. For an attempt Pay Now reopened, the card's whole charge as the session reports it (a statement card split
   * across invoices charged more than this target's share).
   */
  customerTotal: number
}

interface Choosing extends Omit<Charging, 'customerTotal'> {
  /**
   * Null while a tender that carries a shop fee waits for the server's quote. A quote holds only for the session it was
   * priced against, so any new session (a refetch, a refusal, Try Again) drops it until staff's next choice brings a
   * fresh one.
   */
  customerTotal: number | null
  /** Why the server refused the last submit (a stale total, a validation error), until staff change something. */
  refusal: string | null
}

interface Ready extends Charging {
  refusal: string | null
}

/** A payment staff took: the session it was taken against, which its amounts and limits read from until it settles. */
interface Taken extends Charging {
  /**
   * The session when staff pressed Pay. The server's later sessions count this payment as held until it settles, which
   * would leave nothing to collect; Pay Now's amounts read this one meanwhile.
   */
  paidAgainst: PaymentSession
}

interface Submitting extends Taken {
  /** Names this submit, so sending it again after a lost answer is the same request, not a second payment. */
  submissionKey: string
}

interface Attempting extends Taken {
  paymentId: number
}

interface Settled extends Attempting {
  /**
   * The order the payment placed or paid toward, for the success handoff (the session's `order_id`); null when it paid
   * a statement's invoices or part of a cart that isn't placed yet.
   */
  orderId: number | null
  /** The cash the customer gets back, as the server recorded it; 0 for anything else. */
  changeDue: number
}

interface Checking extends Attempting {
  /**
   * Staff can cancel this payment by name: a reader payment while the customer is at the reader, or a typed card Stripe
   * never confirmed (prepared, or waiting on 3D Secure) once a check runs out. Only a session showing this payment
   * changes it: another workstation's newer attempt says nothing about this one.
   */
  cancellable: boolean
  /** Stripe's decline text from confirming this typed card, shown when the server records none of its own. */
  stripeMessage: string | null
}

export type PayNowState =
  /**
   * `unanswered`: a submit staff closed before it got a definite answer. Reopening sends it again under its key before
   * anything new can be collected, since the server may have recorded it.
   */
  | { kind: 'closed'; unanswered: Submitting | null }
  | ({ kind: 'choosing' } & Choosing)
  | ({ kind: 'ready' } & Ready)
  | ({ kind: 'submitting' } & Submitting)
  | ({ kind: 'unanswered' } & Submitting)
  | ({ kind: 'checking' } & Checking)
  | ({ kind: 'unknown' } & Checking)
  /**
   * `placing`: the payment completed, and the server is still placing the order it pays for (another worker holds the
   * placement, or it waits to retry). `unplaced`: it's still placing after Pay Now's wait.
   */
  | ({ kind: 'placing' } & Attempting)
  | ({ kind: 'unplaced' } & Attempting)
  | ({ kind: 'succeeded' } & Settled)
  | ({ kind: 'declined'; message: string } & Attempting)

export type PayNowStateKind = PayNowState['kind']

/** The principal and customer total the server accepted, which can be a cent below what was sent. */
export interface AcceptedCharge {
  amount: number
  customerTotal: number
}

export type PayNowEvent =
  /**
   * Staff open Pay Now; the session is what the server reports now. `resume` names the payment this target's Pay Now
   * was showing when the page reloaded it: Pay Now opens on that payment's outcome again, as the session reports it.
   */
  | { type: 'open'; session: PaymentSession; tender: PayNowTender; resume?: number | null }
  /**
   * Staff choose a tender or type an amount. For a tender that carries a shop fee, `customerTotal` is the server's
   * quote for that amount; otherwise it's left out.
   */
  | { type: 'choose'; tender: PayNowTender; amount: number; customerTotal?: number | null }
  /** Staff edit the amount or tender of a payment that's ready. */
  | { type: 'edit' }
  /** Pay, Send to reader, or Apply cash or check, named by a new submission key. */
  | { type: 'pay'; submissionKey: string }
  /**
   * Stripe or the reader answered, whatever the answer, for this card payment. `stripeMessage` is the decline text
   * Stripe gave when confirming a typed card.
   */
  | {
      type: 'answered'
      paymentId: number
      accepted: AcceptedCharge | null
      stripeMessage: string | null
    }
  /** The cash or check request succeeded: its response is the settled session. */
  | {
      type: 'applied'
      paymentId: number
      session: PaymentSession
      accepted: AcceptedCharge | null
    }
  /** The server refused the submit before any payment existed; `session` is what it reports now. */
  | { type: 'refused'; message: string; session: PaymentSession }
  /**
   * The submit got no definite answer: the connection dropped, or the server is still recording an earlier copy. The
   * server may or may not have recorded it.
   */
  | { type: 'lost' }
  /** Send the unanswered submit again, under the same submission key. */
  | { type: 'resend' }
  /**
   * The server's answer to "check payment X", for the payment it names: its outcome, and for a completed payment whether
   * its order is still being placed (the order it placed or paid toward is the session's).
   */
  | {
      type: 'checked'
      paymentId: number
      outcome: 'completed' | 'failed' | 'processing'
      message: string | null
      session: PaymentSession
      placingOrder: boolean
    }
  /** The server reported the session again (a refetch, an action's response). */
  | { type: 'sessionReported'; session: PaymentSession }
  /** No settled outcome after Pay Now's wait. */
  | { type: 'checkTimedOut' }
  | { type: 'checkStatus' }
  /** Staff cancelled a payment by name; `session` is what the server reports after the cancel. */
  | { type: 'abandoned'; session: PaymentSession }
  | { type: 'tryAgain' }
  | { type: 'addAnother' }
  | { type: 'close' }

export type PayNowEventType = PayNowEvent['type']

/** Why an amount can't be collected with a tender, or null when it can. */
export type AmountProblem =
  | 'enter_amount'
  | 'unpriced'
  | 'over_collectible'
  | 'below_card_minimum'
  | 'leaves_too_little'

/** The one place Pay Now applies the amount rules, with the limits the server sends. */
export function amountProblem(
  session: PaymentSession,
  tender: PayNowTender,
  amount: number
): AmountProblem | null {
  const charge = normalizeCurrencyValue(amount)

  if (!(charge > 0)) {
    return 'enter_amount'
  }

  if (session.collectible_total === null) {
    return 'unpriced'
  }

  const collectible = parseCurrency(session.collectible_total)

  if (charge > collectible) {
    return 'over_collectible'
  }

  if (tender === 'cash' || tender === 'check') {
    return null
  }

  if (charge < parseCurrency(session.card_minimum)) {
    return 'below_card_minimum'
  }

  const leftover = normalizeCurrencyValue(collectible - charge)

  return leftover > 0 && leftover < parseCurrency(session.card_smallest_leftover)
    ? 'leaves_too_little'
    : null
}

/** What can be collected now, or 0 while the session can't say. */
export function collectibleAmount(session: PaymentSession): number {
  return session.collectible_total === null ? 0 : parseCurrency(session.collectible_total)
}

/** Whether this tender carries the target's shop fee, so what the customer pays needs the server's quote. */
export function tenderCarriesShopFee(session: PaymentSession, tender: PayNowTender): boolean {
  const paymentType = tender === 'card' || tender === 'reader' ? 'card' : tender

  return session.shop_fee_payment_types.includes(paymentType)
}

type AttemptOutcome = 'unsettled' | 'completed' | 'failed'

/** Where the session says this payment's attempt stands; an attempt the session no longer shows is unsettled. */
export function attemptOutcome(session: PaymentSession, paymentId: number): AttemptOutcome {
  const attempt = session.attempt

  if (attempt === null || attempt.payment_id !== paymentId) {
    return 'unsettled'
  }

  switch (attempt.state) {
    case 'completed':
      return 'completed'
    case 'failed':
    case 'cancelled':
    case 'expired':
      return 'failed'
    case 'prepared':
    case 'awaiting_customer_action':
    case 'processing':
    case null:
      return 'unsettled'
  }
}

/**
 * A typed card prepared (or waiting on 3D Secure) that Stripe never confirmed: nobody is collecting it but this form,
 * so staff can cancel it by name.
 */
function unconfirmedCard(session: PaymentSession, paymentId: number): boolean {
  const attempt = session.attempt

  return (
    attempt?.payment_id === paymentId &&
    attempt.tender === 'card' &&
    (attempt.state === 'prepared' || attempt.state === 'awaiting_customer_action')
  )
}

/**
 * Whether staff can cancel this payment by name: a typed card Stripe never confirmed, or a reader payment that hasn't
 * settled. The server stops the reader only while it shows this payment, and keeps a charge that already went through.
 */
function cancellableByName(session: PaymentSession, paymentId: number): boolean {
  const attempt = session.attempt

  return (
    unconfirmedCard(session, paymentId) ||
    (attempt?.payment_id === paymentId &&
      attempt.tender === 'reader' &&
      attemptOutcome(session, paymentId) === 'unsettled')
  )
}

const genericDeclinedMessage = 'This payment was declined.'

/** The failure text the session records for this payment; another payment's text never stands in for it. */
/** The server's text for this payment's failure, else Stripe's from confirming it, else the generic one. */
const declinedMessageFor = (
  session: PaymentSession,
  paymentId: number,
  stripeMessage: string | null
): string =>
  (session.attempt?.payment_id === paymentId ? session.attempt.failure_message : null) ??
  stripeMessage ??
  genericDeclinedMessage

const CLOSED: PayNowState = { kind: 'closed', unanswered: null }

/** One row of the plan's table: from a state, on an event, to a state. */
export interface PayNowTransition {
  from: PayNowStateKind
  on: PayNowEventType
  to: PayNowStateKind
  /** What happens, as the plan's table says it. */
  does: string
}

/** The plan's state table, row for row. `transitionPayNow` implements exactly these moves. */
export const PAY_NOW_TRANSITIONS: readonly PayNowTransition[] = [
  { from: 'closed', on: 'open', to: 'ready', does: 'Read the session; the amount fits the tender' },
  {
    from: 'closed',
    on: 'open',
    to: 'choosing',
    does: 'Read the session; staff choose another amount or tender',
  },
  {
    from: 'closed',
    on: 'open',
    to: 'unanswered',
    does: 'A submit closed unanswered: send it again under its key before collecting anything new',
  },
  {
    from: 'closed',
    on: 'open',
    to: 'checking',
    does: 'The session shows an attempt still unsettled: check it',
  },
  {
    from: 'closed',
    on: 'open',
    to: 'placing',
    does: 'Reloaded on a payment that completed: a check says whether its order is placed, then shows its success',
  },
  {
    from: 'closed',
    on: 'open',
    to: 'declined',
    does: 'Reloaded on a payment that failed: show the decline again',
  },
  {
    from: 'choosing',
    on: 'choose',
    to: 'ready',
    does: 'Amount and tender within the session’s limits',
  },
  {
    from: 'choosing',
    on: 'choose',
    to: 'choosing',
    does: 'Outside the limits, or a fee quote still to come',
  },
  {
    from: 'choosing',
    on: 'sessionReported',
    to: 'choosing',
    does: 'A newer session; a fee quote is dropped',
  },
  {
    from: 'choosing',
    on: 'sessionReported',
    to: 'checking',
    does: 'The session shows an attempt still unsettled: check it before collecting more',
  },
  { from: 'ready', on: 'edit', to: 'choosing', does: 'Edit amount or tender' },
  { from: 'ready', on: 'choose', to: 'ready', does: 'Another amount or tender within the limits' },
  { from: 'ready', on: 'choose', to: 'choosing', does: 'Another amount or tender outside them' },
  {
    from: 'ready',
    on: 'pay',
    to: 'submitting',
    does: 'Pay, send to reader, or apply cash or check, under a new key',
  },
  {
    from: 'ready',
    on: 'sessionReported',
    to: 'ready',
    does: 'A newer session that still allows the amount',
  },
  {
    from: 'ready',
    on: 'sessionReported',
    to: 'choosing',
    does: 'A newer session that no longer allows it, or a fee tender that needs a new quote',
  },
  {
    from: 'ready',
    on: 'sessionReported',
    to: 'checking',
    does: 'The session shows an attempt still unsettled: check it before collecting more',
  },
  {
    from: 'submitting',
    on: 'answered',
    to: 'checking',
    does: 'Report “check payment X”, with what the server accepted; read until settled',
  },
  {
    from: 'submitting',
    on: 'applied',
    to: 'succeeded',
    does: 'Cash or check: the response is the settled session',
  },
  {
    from: 'submitting',
    on: 'refused',
    to: 'ready',
    does: 'Refused before any payment existed: show why',
  },
  {
    from: 'submitting',
    on: 'refused',
    to: 'choosing',
    does: 'Refused, and the amount no longer fits what the server reports now, or needs a new quote',
  },
  {
    from: 'submitting',
    on: 'refused',
    to: 'checking',
    does: 'Refused, and the session shows another attempt still unsettled: check it',
  },
  {
    from: 'submitting',
    on: 'lost',
    to: 'unanswered',
    does: 'No definite answer: the server may have recorded it. Nothing is retried as new',
  },
  {
    from: 'unanswered',
    on: 'resend',
    to: 'submitting',
    does: 'Send it again under the same key, which the server answers as the same request',
  },
  {
    from: 'unanswered',
    on: 'close',
    to: 'closed',
    does: 'Keep the unanswered submit, so reopening asks about it before anything new',
  },
  {
    from: 'checking',
    on: 'checked',
    to: 'succeeded',
    does: 'The server says the payment it checked completed and its order is placed',
  },
  {
    from: 'checking',
    on: 'checked',
    to: 'placing',
    does: 'It completed, and the server is still placing its order: keep checking',
  },
  { from: 'checking', on: 'checked', to: 'declined', does: 'It failed: show the server’s message' },
  { from: 'checking', on: 'checked', to: 'checking', does: 'It isn’t settled yet' },
  {
    from: 'checking',
    on: 'checked',
    to: 'unknown',
    does: 'Stripe never confirmed the card (prepared, or waiting on 3D Secure): staff check again or cancel it',
  },
  {
    from: 'checking',
    on: 'sessionReported',
    to: 'placing',
    does: 'The session shows the attempt completed: check that its order is placed',
  },
  {
    from: 'checking',
    on: 'sessionReported',
    to: 'declined',
    does: 'The session shows the attempt failed',
  },
  {
    from: 'checking',
    on: 'sessionReported',
    to: 'checking',
    does: 'The attempt isn’t settled yet',
  },
  {
    from: 'checking',
    on: 'checkTimedOut',
    to: 'unknown',
    does: 'Show “Still checking this payment”',
  },
  {
    from: 'checking',
    on: 'abandoned',
    to: 'ready',
    does: 'Staff cancelled the reader payment by name while the customer was at the reader; collect again',
  },
  {
    from: 'checking',
    on: 'abandoned',
    to: 'choosing',
    does: 'Cancelled; the amount no longer fits, or needs a new quote',
  },
  {
    from: 'checking',
    on: 'abandoned',
    to: 'checking',
    does: 'Cancelled, and the session shows another attempt still unsettled: check it',
  },
  {
    from: 'checking',
    on: 'abandoned',
    to: 'placing',
    does: 'The cancel came too late: the payment completed first and the server kept it, so check its order is placed',
  },
  { from: 'unknown', on: 'checkStatus', to: 'checking', does: 'Check the attempt again' },
  {
    from: 'unknown',
    on: 'abandoned',
    to: 'ready',
    does: 'Staff cancelled the reader payment or unconfirmed card by name; collect again',
  },
  {
    from: 'unknown',
    on: 'abandoned',
    to: 'choosing',
    does: 'Cancelled; the amount no longer fits, or needs a new quote',
  },
  {
    from: 'unknown',
    on: 'abandoned',
    to: 'checking',
    does: 'Cancelled, and the session shows another attempt still unsettled: check it',
  },
  {
    from: 'unknown',
    on: 'abandoned',
    to: 'placing',
    does: 'The cancel came too late: the payment completed first and the server kept it, so check its order is placed',
  },
  {
    from: 'unknown',
    on: 'sessionReported',
    to: 'placing',
    does: 'The session shows the payment completed: check that its order is placed',
  },
  {
    from: 'unknown',
    on: 'sessionReported',
    to: 'declined',
    does: 'The session shows it failed',
  },
  {
    from: 'unknown',
    on: 'sessionReported',
    to: 'unknown',
    does: 'Still unsettled; a card that reached Stripe can no longer be cancelled',
  },
  {
    from: 'declined',
    on: 'tryAgain',
    to: 'ready',
    does: 'Nothing to cancel: the session shows the released balance. The amount tried, within it',
  },
  {
    from: 'declined',
    on: 'tryAgain',
    to: 'choosing',
    does: 'Nothing to cancel; the amount no longer fits, or needs a new quote',
  },
  {
    from: 'declined',
    on: 'tryAgain',
    to: 'checking',
    does: 'The session shows an attempt still unsettled: check it before collecting more',
  },
  {
    from: 'declined',
    on: 'sessionReported',
    to: 'placing',
    does: 'A late receipt completed the payment after all: check its order, then show the success',
  },
  {
    from: 'declined',
    on: 'sessionReported',
    to: 'declined',
    does: 'Keep the decline; Try Again starts from what the server reports now',
  },
  {
    from: 'placing',
    on: 'checked',
    to: 'succeeded',
    does: 'The server placed the order: show the success',
  },
  { from: 'placing', on: 'checked', to: 'placing', does: 'The order is still being placed' },
  { from: 'placing', on: 'sessionReported', to: 'placing', does: 'A newer session; keep checking' },
  {
    from: 'placing',
    on: 'checkTimedOut',
    to: 'unplaced',
    does: 'Show that the payment went through and its order is still being placed',
  },
  {
    from: 'unplaced',
    on: 'checkStatus',
    to: 'placing',
    does: 'Check again, which places a pending order',
  },
  {
    from: 'unplaced',
    on: 'sessionReported',
    to: 'unplaced',
    does: 'A newer session; wait for Check status',
  },
  { from: 'succeeded', on: 'addAnother', to: 'ready', does: 'Collect what remains' },
  {
    from: 'succeeded',
    on: 'addAnother',
    to: 'choosing',
    does: 'What remains doesn’t fit the tender',
  },
  {
    from: 'succeeded',
    on: 'addAnother',
    to: 'checking',
    does: 'The session shows an attempt still unsettled: check it before collecting more',
  },
  {
    from: 'succeeded',
    on: 'sessionReported',
    to: 'succeeded',
    does: 'Keep the success; Add another payment starts from what the server reports now',
  },
  { from: 'choosing', on: 'close', to: 'closed', does: 'Nothing to clean up' },
  {
    from: 'ready',
    on: 'close',
    to: 'closed',
    does: 'Nothing to clean up; a card is prepared only when staff pay',
  },
  { from: 'unknown', on: 'close', to: 'closed', does: 'The server owns the open attempt' },
  {
    from: 'unplaced',
    on: 'close',
    to: 'closed',
    does: 'The payment went through; the server places the order',
  },
  { from: 'succeeded', on: 'close', to: 'closed', does: 'Done' },
  { from: 'declined', on: 'close', to: 'closed', does: 'Nothing to clean up' },
]

/** Whether the session shows an attempt still unsettled, which Pay Now picks up when it opens. */
export function hasUnsettledAttempt(session: PaymentSession): boolean {
  return checkingUnsettled(session) !== null
}

/**
 * The attempt the session shows still unsettled, checked as this target's payment: what it puts toward the target (a
 * split statement card's share, not its whole charge) and what the card charged. Null when there's none.
 */
function checkingUnsettled(session: PaymentSession): PayNowState | null {
  const attempt = session.attempt
  if (attempt === null || attemptOutcome(session, attempt.payment_id) !== 'unsettled') {
    return null
  }

  return {
    kind: 'checking',
    session,
    tender: attempt.tender,
    amount: parseCurrency(attempt.principal),
    customerTotal: parseCurrency(attempt.charge),
    // Found already held, so this session is the closest to the one it was taken against.
    paidAgainst: session,
    paymentId: attempt.payment_id,
    cancellable: cancellableByName(session, attempt.payment_id),
    stripeMessage: null,
  }
}

/**
 * Where every collection starts. Split tenders are sequential, so while the session shows an attempt still unsettled
 * (another workstation's card, or one a refresh found), Pay Now checks it instead of collecting more.
 */
function collecting(
  session: PaymentSession,
  tender: PayNowTender,
  amount: number,
  customerTotal: number | null = null,
  refusal: string | null = null
): PayNowState {
  const unsettled = checkingUnsettled(session)
  if (unsettled) {
    return unsettled
  }
  // The amount kept is the one validated, to the cent.
  const charge = normalizeCurrencyValue(amount)
  const total = tenderCarriesShopFee(session, tender)
    ? customerTotal === null
      ? null
      : normalizeCurrencyValue(customerTotal)
    : charge
  const fields = { session, tender, amount: charge, refusal }

  return amountProblem(session, tender, charge) === null && total !== null
    ? { kind: 'ready', ...fields, customerTotal: total }
    : { kind: 'choosing', ...fields, customerTotal: total }
}

/** What a payment carries once the server answered: what it accepted, when it said. */
function charged(
  state: Charging,
  accepted: AcceptedCharge | null
): Pick<Charging, 'amount' | 'customerTotal'> {
  return accepted
    ? { amount: accepted.amount, customerTotal: accepted.customerTotal }
    : { amount: state.amount, customerTotal: state.customerTotal }
}

/** The cash the session says this payment handed back, or 0. */
function changeDueFor(session: PaymentSession, paymentId: number): number {
  const attempt = session.attempt

  return attempt?.payment_id === paymentId && attempt.change_due !== null
    ? parseCurrency(attempt.change_due)
    : 0
}

/** A payment that settled with its money: the order it placed or paid toward and any change, from the session. */
function succeeded(fields: Attempting): PayNowState {
  return {
    kind: 'succeeded',
    ...fields,
    orderId: fields.session.order_id,
    changeDue: changeDueFor(fields.session, fields.paymentId),
  }
}

/**
 * A completed payment: succeeded once the server says its order is placed, and placing until then. `placingOrder` is
 * a check's answer; null (a session showing it completed) leaves it placing until a check says.
 */
function completed(
  { session, tender, amount, customerTotal, paidAgainst, paymentId }: Attempting,
  placingOrder: boolean | null
): PayNowState {
  const fields = { session, tender, amount, customerTotal, paidAgainst, paymentId }

  return placingOrder === null || placingOrder ? { kind: 'placing', ...fields } : succeeded(fields)
}

/**
 * Where a payment the page reloaded Pay Now on stands, as the session reports it: a decline shows again; a completed
 * payment waits on its order until a check says it's placed (the check then shows its success, from the session); one
 * still open is checked. Null when the session no longer shows it.
 */
function resumed(session: PaymentSession, paymentId: number): PayNowState | null {
  const attempt = session.attempt
  if (attempt === null || attempt.payment_id !== paymentId) {
    return null
  }
  const fields = {
    session,
    tender: attempt.tender,
    amount: parseCurrency(attempt.principal),
    customerTotal: parseCurrency(attempt.charge),
    paidAgainst: session,
    paymentId,
  }

  switch (attemptOutcome(session, paymentId)) {
    case 'completed':
      return completed(fields, null)
    case 'failed':
      return { kind: 'declined', ...fields, message: declinedMessageFor(session, paymentId, null) }
    case 'unsettled':
      return checkingUnsettled(session)
  }
}

/**
 * Where the payment being checked stands now: settled from the server's answer or session, or still open. Only a check
 * says whether a completed payment's order is placed (`placingOrder`); a session showing it completed leaves it placing.
 */
function settled(
  state: Extract<PayNowState, { kind: 'checking' | 'unknown' }>,
  session: PaymentSession,
  outcome: AttemptOutcome | 'processing',
  message: string | null,
  placingOrder: boolean | null
): PayNowState {
  const attempt = {
    session,
    tender: state.tender,
    amount: state.amount,
    customerTotal: state.customerTotal,
    paidAgainst: state.paidAgainst,
    paymentId: state.paymentId,
  }

  switch (outcome) {
    case 'completed':
      return completed(attempt, placingOrder)
    case 'failed':
      return {
        kind: 'declined',
        ...attempt,
        message: message ?? declinedMessageFor(session, state.paymentId, state.stripeMessage),
      }
    case 'unsettled':
    case 'processing':
      return {
        ...state,
        session,
        cancellable:
          session.attempt?.payment_id === state.paymentId
            ? cancellableByName(session, state.paymentId)
            : state.cancellable,
      }
  }
}

/** A payment staff cancelled by name: collect again with the amount tried, within what's collectible now. */
function abandon(
  state: Extract<PayNowState, { kind: 'checking' | 'unknown' }>,
  session: PaymentSession
): PayNowState {
  if (!state.cancellable) {
    return state
  }
  // A cancel that came too late: the customer finished paying first, and the server kept the payment. Pay Now shows
  // that payment's outcome instead of collecting again.
  if (attemptOutcome(session, state.paymentId) === 'completed') {
    return settled(state, session, 'completed', null, null)
  }

  return collecting(session, state.tender, Math.min(state.amount, collectibleAmount(session)))
}

/**
 * Try Again keeps the amount tried, within what's collectible now. A fee quote doesn't carry over: the released
 * attempt and any changed fee settings mean the next card is priced again.
 */
function retry(state: Extract<PayNowState, { kind: 'declined' }>): PayNowState {
  return collecting(
    state.session,
    state.tender,
    Math.min(state.amount, collectibleAmount(state.session))
  )
}

function fromClosed(
  state: Extract<PayNowState, { kind: 'closed' }>,
  event: PayNowEvent
): PayNowState {
  if (event.type !== 'open') {
    return state
  }
  if (state.unanswered) {
    return { ...state.unanswered, kind: 'unanswered', session: event.session }
  }
  const resumedState =
    event.resume === undefined || event.resume === null
      ? null
      : resumed(event.session, event.resume)

  return resumedState ?? collecting(event.session, event.tender, collectibleAmount(event.session))
}

function fromChoosing(
  state: Extract<PayNowState, { kind: 'choosing' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'choose':
      return collecting(state.session, event.tender, event.amount, event.customerTotal ?? null)
    case 'sessionReported':
      // Staff may be mid-edit, so a refresh doesn't move them on (unless an attempt needs checking); it only drops a
      // quote priced against the old session.
      return (
        checkingUnsettled(event.session) ?? {
          ...state,
          session: event.session,
          customerTotal: tenderCarriesShopFee(event.session, state.tender) ? null : state.amount,
        }
      )
    case 'close':
      return CLOSED
    case 'open':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromReady(
  state: Extract<PayNowState, { kind: 'ready' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'edit':
      return { ...state, kind: 'choosing' }
    case 'choose':
      return collecting(state.session, event.tender, event.amount, event.customerTotal ?? null)
    case 'pay':
      return {
        kind: 'submitting',
        session: state.session,
        tender: state.tender,
        amount: state.amount,
        customerTotal: state.customerTotal,
        paidAgainst: state.session,
        submissionKey: event.submissionKey,
      }
    case 'sessionReported':
      // A fee quote holds only for the session it was priced against.
      return collecting(event.session, state.tender, state.amount, null, state.refusal)
    case 'close':
      return CLOSED
    case 'open':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromSubmitting(
  state: Extract<PayNowState, { kind: 'submitting' }>,
  event: PayNowEvent
): PayNowState {
  // Close is blocked while a payment is submitting: the only ways on are the answers.
  switch (event.type) {
    case 'answered':
      return {
        kind: 'checking',
        session: state.session,
        tender: state.tender,
        ...charged(state, event.accepted),
        paidAgainst: state.paidAgainst,
        paymentId: event.paymentId,
        // The reader shows the payment it just took, so staff can cancel it while the customer is there. Stripe just
        // answered for a typed card; the first check says whether it still waits on the customer.
        cancellable: state.tender === 'reader',
        stripeMessage: event.stripeMessage,
      }
    case 'applied':
      return succeeded({
        session: event.session,
        tender: state.tender,
        ...charged(state, event.accepted),
        paidAgainst: state.paidAgainst,
        paymentId: event.paymentId,
      })
    case 'refused':
      // Revalidated against what the server reports now; a fee quote is priced again.
      return collecting(event.session, state.tender, state.amount, null, event.message)
    case 'lost':
      return { ...state, kind: 'unanswered' }
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'resend':
    case 'checked':
    case 'sessionReported':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
    case 'close':
      return state
  }
}

function fromUnanswered(
  state: Extract<PayNowState, { kind: 'unanswered' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'resend':
      return { ...state, kind: 'submitting' }
    case 'close':
      return { kind: 'closed', unanswered: state }
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'checked':
    case 'sessionReported':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromChecking(
  state: Extract<PayNowState, { kind: 'checking' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'checked':
      if (event.paymentId !== state.paymentId) {
        return state
      }
      // Nobody else is collecting a card Stripe never confirmed: staff decide whether to check again or cancel it.
      if (event.outcome === 'processing' && unconfirmedCard(event.session, state.paymentId)) {
        return { ...state, kind: 'unknown', session: event.session, cancellable: true }
      }
      return settled(state, event.session, event.outcome, event.message, event.placingOrder)
    case 'sessionReported':
      return settled(
        state,
        event.session,
        attemptOutcome(event.session, state.paymentId),
        null,
        null
      )
    case 'checkTimedOut':
      return { ...state, kind: 'unknown' }
    case 'abandoned':
      return abandon(state, event.session)
    // Close waits for the card's answer, as Pay Now always has: staff see the outcome before they move on. Once the wait
    // runs out (`unknown`), they can close and leave the card to the server.
    case 'close':
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checkStatus':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromUnknown(
  state: Extract<PayNowState, { kind: 'unknown' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'checkStatus':
      return {
        kind: 'checking',
        session: state.session,
        tender: state.tender,
        amount: state.amount,
        customerTotal: state.customerTotal,
        paidAgainst: state.paidAgainst,
        paymentId: state.paymentId,
        cancellable: state.cancellable,
        stripeMessage: state.stripeMessage,
      }
    case 'sessionReported':
      // A webhook or the server's own checks can settle the payment while staff look at this screen.
      return settled(
        state,
        event.session,
        attemptOutcome(event.session, state.paymentId),
        null,
        null
      )
    case 'abandoned':
      return abandon(state, event.session)
    case 'close':
      return CLOSED
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromPlacing(
  state: Extract<PayNowState, { kind: 'placing' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'checked':
      if (event.paymentId !== state.paymentId) {
        return state
      }
      // The payment completed already; a check only says whether its order is placed yet.
      return event.outcome === 'completed'
        ? completed({ ...state, session: event.session }, event.placingOrder)
        : { ...state, session: event.session }
    case 'sessionReported':
      return { ...state, session: event.session }
    case 'checkTimedOut':
      return { ...state, kind: 'unplaced' }
    // Close waits for the order, as Pay Now's finalizing always has; once the wait runs out (`unplaced`) staff can close.
    case 'close':
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromUnplaced(
  state: Extract<PayNowState, { kind: 'unplaced' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'checkStatus':
      return { ...state, kind: 'placing' }
    case 'sessionReported':
      return { ...state, session: event.session }
    case 'close':
      return CLOSED
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'abandoned':
    case 'tryAgain':
    case 'addAnother':
      return state
  }
}

function fromDeclined(
  state: Extract<PayNowState, { kind: 'declined' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'tryAgain':
      return retry(state)
    case 'sessionReported':
      // A late receipt can complete a payment the server had recorded as failed: the customer was charged after all.
      // Otherwise the decline stays on screen, and Try Again starts from what the server reports now.
      return attemptOutcome(event.session, state.paymentId) === 'completed'
        ? {
            kind: 'placing',
            session: event.session,
            tender: state.tender,
            amount: state.amount,
            customerTotal: state.customerTotal,
            paidAgainst: state.paidAgainst,
            paymentId: state.paymentId,
          }
        : { ...state, session: event.session }
    case 'close':
      return CLOSED
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'addAnother':
      return state
  }
}

function fromSucceeded(
  state: Extract<PayNowState, { kind: 'succeeded' }>,
  event: PayNowEvent
): PayNowState {
  switch (event.type) {
    case 'addAnother':
      return collecting(state.session, state.tender, collectibleAmount(state.session))
    case 'sessionReported':
      // The success stays on screen; Add another payment starts from what the server reports now.
      return { ...state, session: event.session }
    case 'close':
      return CLOSED
    case 'open':
    case 'choose':
    case 'edit':
    case 'pay':
    case 'answered':
    case 'applied':
    case 'refused':
    case 'lost':
    case 'resend':
    case 'checked':
    case 'checkTimedOut':
    case 'checkStatus':
    case 'abandoned':
    case 'tryAgain':
      return state
  }
}

/**
 * Pay Now's one transition function. An event the table doesn't list for the current state returns that state
 * unchanged, so nothing outside the table can move Pay Now.
 */
export function transitionPayNow(state: PayNowState, event: PayNowEvent): PayNowState {
  switch (state.kind) {
    case 'closed':
      return fromClosed(state, event)
    case 'choosing':
      return fromChoosing(state, event)
    case 'ready':
      return fromReady(state, event)
    case 'submitting':
      return fromSubmitting(state, event)
    case 'unanswered':
      return fromUnanswered(state, event)
    case 'checking':
      return fromChecking(state, event)
    case 'unknown':
      return fromUnknown(state, event)
    case 'placing':
      return fromPlacing(state, event)
    case 'unplaced':
      return fromUnplaced(state, event)
    case 'declined':
      return fromDeclined(state, event)
    case 'succeeded':
      return fromSucceeded(state, event)
  }
}
